import * as React from 'react';

import { ISearchContext, FilterParameterTypeString } from '../../global/models/search-context';
import { ISearchField, SearchOperator } from '../models/search-field';

import { IEpiProps } from '../models/epi-props';
import { IAnalytics } from '../../global/functions/analytics';
import { SearchPreview } from './search-preview';
import { SearchContext } from './search-context';
import { SearchButtons } from './search-buttons';
import { ContextSwitchDialog } from './context-switch-dialog';
import { SearchOperatorView } from './search-operator-view';

interface IProps {
    epiProps: IEpiProps,
    searchContexts: ISearchContext[],
    initialSearchField: ISearchField
    analytics: IAnalytics;

    onSearch: (context: ISearchContext, query: string) => void
}

interface IState {
    searchContextIndex: number,
    initialSearchField: ISearchField,
    searchFields: ISearchField[],

    showContextDialog: boolean,
    contextDialogContent: string,
    nextSearchFields: ISearchField[],
    nextSearchContextIndex: number
}

export class AdvancedSearchForm extends React.Component<IProps, IState> {

    state: IState = {
        searchContextIndex: 0,
        initialSearchField: this.props.initialSearchField,
        searchFields: [],

        showContextDialog: false,
        contextDialogContent: '',
        nextSearchFields: [],
        nextSearchContextIndex: 0
    }

    get currentFilters() {
        return this.props.searchContexts[this.state.searchContextIndex].filters;
    }

    render() {

        const { searchContexts } = this.props;
        const { searchContextIndex, initialSearchField } = this.state;

        if (searchContexts.length === 0) {
            return <h3>Loading Advanced Search...</h3>
        }

        const {
            searchLabel,
            searchHint,
            addAnotherLabel,
            buildSearchPrompt,
            buildSearchHint,
            searchFromHint,
            searchToHint
        } = this.props.epiProps;

        const fullSearchString = this.generateSearchString();

        return (
            <div className="advanced-search-form-container">
                {this.renderContextSwitchDialog()}
                <h3>Your Search</h3>
                <form noValidate>
                    <SearchPreview
                        prompt={buildSearchPrompt}
                        placeholder={buildSearchHint}
                        searchString={fullSearchString} />
                    <hr />
                    <SearchContext
                        searchContexts={searchContexts}
                        selectedContextIndex={searchContextIndex}
                        searchField={initialSearchField}
                        searchFilters={this.currentFilters}
                        onContextChange={this.onContextChange}
                        epiProps={this.props.epiProps}
                        onSearchFieldChange={this.onInitialSearchChange}
                        onKeyDown={this.onKeyDown} />
                    <hr />
                    {this.state.searchFields.map(this.renderSearchField)}
                    <SearchButtons
                        addSearchFieldLabel={addAnotherLabel}
                        searchLabel={searchLabel}
                        onAddSearchField={this.onAddSearchField}
                        onSearch={this.onSearch} />
                </form>
            </div>
        );
    }

    renderContextSwitchDialog = () => {
        const { changeContextTitle } = this.props.epiProps;
        const { contextDialogContent } = this.state;
        
        if (this.state.showContextDialog) {
            return (
                <ContextSwitchDialog
                    title={changeContextTitle}
                    content={contextDialogContent}
                    onCancel={this.onContextSwitchCancel}
                    onContinue={this.onContextSwitchContinue} />
            )
        }
    }

    renderSearchField = (searchField, index) => {
        const { searchHint, searchFromHint, searchToHint } = this.props.epiProps;

        const onChange = (searchField: ISearchField) => this.onChangeSearchField(index, searchField);
        const onRemove = () => this.onRemoveSearchField(index);

        return (
            <React.Fragment key={index}>
                <SearchOperatorView
                    name={`adv-search-field-${index}`}
                    epiProps={this.props.epiProps}
                    searchField={searchField}
                    searchFilters={this.currentFilters}
                    onChange={onChange}
                    onRemove={onRemove}
                    onKeyDown={this.onKeyDown} />
                <hr/>
            </React.Fragment>
        )
    }

    generateSearchString = (): string => {
        const { initialSearchField, searchFields } = this.state;

        if (initialSearchField.query === '') {
            return '';
        }

        const initialTerm = initialSearchField.filter.lexeme + initialSearchField.query + ')';

        const searchTerms = [initialTerm];
        searchFields.forEach(searchField => {
            if (searchField.query.length === 0) {
                return;
            }

            const term = searchField.operator === SearchOperator.Exactly
                ? `"${searchField.query}"`
                : searchField.query;

            const filtered = searchField.filter.lexeme + term + ')';

            switch (searchField.operator.toUpperCase()) {
                case SearchOperator.And:
                case SearchOperator.Exactly:
                    searchTerms.push(`AND ${filtered}`);
                    break;
                case SearchOperator.Or:
                    searchTerms.push(`OR ${filtered}`);
                    break;
                case SearchOperator.Not:
                    searchTerms.push(`AND NOT ${filtered}`);
                    break;
            }
        })

        return searchTerms.join(' ');
    }

    onKeyDown = (e: any) => {
        if (e.keyCode == 13) {
            e.preventDefault();
            e.stopPropagation();
            this.onSearch();
        }
    }

    // When the context is changed we need to check what filters are being used
    // and see if any of them will become invalid, prompting the user about that
    onContextChange = (searchContextIndex: number) => {
        // Make life a little easier
        const { searchContexts } = this.props;

        // Filters of the context we're trying to change to
        const contextFilters = searchContexts[searchContextIndex].filters.map(f => f.id);

        // Filters currently in use
        const currentFilters = this.state.searchFields.map(f => f.filter.id)
            // Make it into a set
            .filter((value, index, self) => self.indexOf(value) === index);

        // Filters currently in use not available to the new context
        const missingFilters = currentFilters.filter(f => contextFilters.indexOf(f) === -1);

        // Nothing missing, we're good
        if (missingFilters.length === 0) {
            this.setState({ searchContextIndex: searchContextIndex })
            return;
        }

        // Text of filters to be removed
        const missingText = searchContexts[this.state.searchContextIndex].filters
            .filter(f => missingFilters.indexOf(f.id) !== -1)
            .map(f => f.display);

        // Grab the name
        const contextName = searchContexts[searchContextIndex].display;

        // Remove fields
        const searchFields = this.state.searchFields.slice().filter(f => missingFilters.indexOf(f.filter.id) === -1);
        // Update prompt
        const content = this.props.epiProps.changeContextPrompt
            ? this.props.epiProps.changeContextPrompt
                .replace('{{context}}', contextName)
                .replace('{{fields}}', missingText.join(', '))
            : '';

        this.setState({
            showContextDialog: true,
            contextDialogContent: content,
            nextSearchFields: searchFields,
            nextSearchContextIndex: searchContextIndex
        });
    }

    onContextSwitchCancel = () => {
        this.setState({
            showContextDialog: false
        });
    }

    onContextSwitchContinue = () => {
        const { nextSearchFields, nextSearchContextIndex } = this.state;
        this.setState({
            showContextDialog: false,
            searchFields: nextSearchFields,
            searchContextIndex: nextSearchContextIndex
        });
    }


    onInitialSearchChange = (searchField: ISearchField) => {
        this.setState({ initialSearchField: searchField })
    }

    // Search fields
    onAddSearchField = e => {
        e.preventDefault();
        this.props.analytics.onAddAnotherBoolean();
        const searchFields = this.state.searchFields.slice();
        searchFields.push(this.props.initialSearchField);
        this.setState({ searchFields: searchFields });
    }

    onChangeSearchField = (index: number, searchField: ISearchField) => {
        const searchFields = this.state.searchFields.slice();
        searchFields[index] = searchField;
        this.setState({ searchFields: searchFields });
    }

    onRemoveSearchField = (index: number) => {
        const searchFields = this.state.searchFields.slice();
        searchFields.splice(index, 1);
        this.setState({ searchFields: searchFields });
    }

    // Actions
    onSearch = () => {

        const { searchContexts } = this.props;
        const { searchContextIndex, searchFields, initialSearchField } = this.state;

        if (!initialSearchField.query) {
            alert('Please enter a search string');
            return;
        }

        // Grab the filters for the context
        const filters = searchContexts[searchContextIndex].filters;

        const validatedFields = searchFields.map(this.validate);

        const errors = validatedFields.map(({valid}, index) => {
            return valid
            ? null
            : searchFields[index].filter.lexeme + searchFields[index].query + ')'
        }).filter(err => err !== null);

        if (errors.length > 0) {
            this.setState({
                searchFields: validatedFields
            });
            return;
        }

        // alert('Seach for: ' + this.generateSearchString())

        this.props.onSearch(searchContexts[searchContextIndex], this.generateSearchString());
    }

    validate = (searchField: ISearchField): ISearchField => {
        const splitQuery = searchField.query.split('-');

        // Validate based on filter parameter type
        switch (searchField.filter.parameterType.typeString) {

            // String
            case FilterParameterTypeString.String:
                // No regex? No validation
                if (!searchField.filter.parameterType.regex) {
                    break;
                }

                // Return filter with query on fail
                const regex = new RegExp(searchField.filter.parameterType.regex);
                searchField.valid = regex.test(searchField.query);
                break; 

            // Number
            case FilterParameterTypeString.Number:
                const numbers = splitQuery.map(q => parseInt(q));
                searchField.valid = this.validateSplitNumbers(splitQuery, numbers);
                break; 

            // Date
            case FilterParameterTypeString.Date:
                const dates = splitQuery.map(q => Date.parse(q));
                searchField.valid = this.validateSplitNumbers(splitQuery, dates);
                break;
            default:
                break;
        }

        return searchField;
    }

    validateSplitNumbers = (splitQuery: string[], splitNumbers: number[]) => {
        // If parsing failed and the query had a value, there was an error
        if (isNaN(splitNumbers[0]) && splitQuery[0] || isNaN(splitNumbers[1]) && splitQuery[1]) {
            return false;
        }

        // If there are two values, the first must be less than the second
        if (splitQuery[0] && splitQuery[1]) {
            return splitNumbers[0] <= splitNumbers[1]
        }

        // Just one numeric input, we're good
        return true;
    }
}